import { Box, Chip, Typography, Button } from "@mui/joy";
import { Clock, PackageCheck, FileDown, RotateCw } from "lucide-react";
import { useParams } from "react-router-dom";
import moment from "moment";

import { RoofMeasurementTable } from "./RoofMeasurementTable";
import { VisualMeasurement } from "./VisualMeasurement";
import { TaskActionComponent } from "../../../components/Tasks/components";
import { PropertyPhotos } from "./PropertyPhotos";
import { InitialInspectionReport } from "./InitialInspectionReport";

import "./index.scss";

interface IMeasurement {
    Id: string;
    MeasurementType: string;
    CalculatedValue: string | number;
    ActualValue: string | number | null | undefined;
}

interface IAttribute {
    Id: string;
    AttributeName: string;
    AttributeValue: any;
}

interface ITaskComponent {
    activeTask: any;
    measurements: IMeasurement[];
    setMeasurements: (e: any) => void;
    attributes: IAttribute[];
    setAttributes: (e: any) => void;
    onComplete: () => void;
}

export default function TaskComponent(props: ITaskComponent) {
    const { activeTask, measurements, setMeasurements, attributes, setAttributes, onComplete } = props;
    const { CreatedAt, Status } = activeTask;

    return (
        <Box>
            <Box mt={2} ml={1} mr={1}>
                <Typography startDecorator={<PackageCheck size={16} />} level="body-sm" gutterBottom>
                    Task
                </Typography>
                <Typography level="h4" sx={{ fontWeight: 500, fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
                    Initial Roof Inspection
                </Typography>

                <Box mt={2} display={"flex"} gap={1} flexWrap="wrap" alignItems="center">
                    <Chip
                        color={Status === "COMPLETED" ? "success" : "warning"}
                        sx={{ fontSize: 12, border: "1px solid", borderColor: "divider" }}
                    >
                        {Status}
                    </Chip>
                    <Chip color="neutral" variant="outlined" sx={{ fontSize: 12 }} startDecorator={<Clock size={14} />}>
                        Created On: {moment(CreatedAt).format("MM/DD/YYYY")}
                    </Chip>
                    <Button 
                        size="sm" 
                        variant="soft" 
                        color="primary" 
                        sx={{ ml: "auto" }}
                        onClick={() => window.dispatchEvent(new CustomEvent('switch-to-v2'))}
                    >
                        Try Inspection V2 (Beta)
                    </Button>
                </Box>

                <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                    <Box flex={1}>
                        <Typography level={"body-sm"} sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                            Description
                        </Typography>
                        <Typography level={"body-md"} sx={{ color: "text.primary" }}>
                            The primary goal of an Initial Roof Inspection is to assess the overall condition of a roof,
                            identify potential issues, document findings, and prepare a detailed report. This inspection
                            serves as a baseline evaluation for maintenance, repair, or replacement decisions.
                        </Typography>
                    </Box>
                </Box>

                <InitialInspectionReport />
                <Box>
                    <Box flex={1} mt={2}>
                        <Typography level={"body-sm"} sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                            Photos
                        </Typography>
                        <Typography level={"body-md"} sx={{ color: "text.primary" }}>
                            Upload clear and detailed photos taken during the initial roof inspection to document its
                            current condition. These images will help assess any existing damages, structural concerns,
                            or areas requiring maintenance.
                        </Typography>
                        <Box mt={1}>
                            <PropertyPhotos taskDetails={activeTask} />
                        </Box>

                        <Box mt={2}>
                            <Typography level={"body-sm"} sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                                Measurements
                            </Typography>
                            <Typography level={"body-md"} sx={{ color: "text.primary" }}>
                                Enter detailed measurements and visual counts from the inspection.
                            </Typography>
                            <Box display={"flex"} flexDirection={"column"} gap={2} mt={3}>
                                <RoofMeasurementTable measurements={measurements} setMeasurements={setMeasurements} />
                                <VisualMeasurement
                                    measurements={measurements}
                                    setMeasurements={setMeasurements}
                                    attributes={attributes}
                                    setAttributes={setAttributes}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <TaskActionComponent taskName={"Initial Inspection"} onComplete={onComplete} isNextEligible={true} taskId={activeTask?.Id} />
            </Box>
        </Box>
    );
}
